import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { Subject } from 'rxjs';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { DatePipe } from '@angular/common';
import {
	DateRangePeriods,
	IAuDateRangePeriods,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/date-range-filter/date-range-periods/date-range-periods.class';
import { MatMenuItem, MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { NgxTolgeeModule } from '@tolgee/ngx';

@Component({
	selector: 'date-range-filter',
	templateUrl: './date-range-filter.component.html',
	styleUrls: ['./date-range-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatMenuItem, MatMenuTrigger, MatMenu, MatCalendar, NgxTolgeeModule],
})
export class DateRangeFilterComponent implements ICmmsFilterComponent, OnDestroy, OnInit {
	@Input() data: { preselectedValue: { start: number; end: number }; columnName: string };
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	selectedDateRange: DateRange<Date>;
	dateRangePeriods: DateRangePeriods;

	private destroy$ = new Subject<void>();

	constructor(private datePipe: DatePipe) {
		this.dateRangePeriods = new DateRangePeriods();
	}

	ngOnInit() {
		if (this.data.preselectedValue) {
			const { start, end } = this.data.preselectedValue;
			setTimeout(() => {
				this.selectedDateRange = new DateRange(new Date(start), new Date(end));
				this.filterValueChanged.emit(this.generateFilterValue());
			});
		}
	}

	selectedChange(date: Date) {
		const start = this.selectedDateRange?.start;
		const end = this.selectedDateRange?.end;

		// condition that checks if the selected date can be placed as the end in range,
		// or we should start new selection with date at the beginning
		if (start && date > start && !end) {
			this.selectedDateRange = new DateRange(start, date);
		} else {
			this.selectedDateRange = new DateRange(date, null);
		}

		this.filterValueChanged.emit(this.generateFilterValue());
	}

	selectQuickDateRange(period: IAuDateRangePeriods) {
		// If periods are equal it means it's one day selection
		if (period.start === period.end) {
			this.selectedDateRange = new DateRange(period.start, null);
			this.filterValueChanged.emit(this.generateFilterValue());
			return;
		}

		this.selectedDateRange = new DateRange(period.start, period.end);
		this.filterValueChanged.emit(this.generateFilterValue());
	}

	convertDateToISOStringWithoutTimezone(date: Date): string {
		return date ? this.datePipe.transform(date, 'yyyy-MM-dd') : null;
	}

	clearDateRange() {
		this.selectedDateRange = null;
	}

	private generateFilterValue(): ICmmsFilterEmittedValue {
		const { start, end } = this.selectedDateRange;
		let label: string;

		if (!end) {
			label = `${this.datePipe.transform(start, 'MMM d')}`;
		} else {
			// prettier-ignore
			label = `${this.datePipe.transform(start, 'MMM d')} - ${this.datePipe.transform(end, 'MMM d')}`;
		}

		return {
			columnName: this.data.columnName,
			label,
			data: {
				start: this.convertDateToISOStringWithoutTimezone(start),
				end: this.convertDateToISOStringWithoutTimezone(end),
			},
		};
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
